/**
 * Legal Store
 *
 * Manages cookie consent and legal preferences, persisted to localStorage.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  CookieConsentStatus,
  CookiePreferences,
} from '../types'
import { DEFAULT_COOKIE_PREFERENCES } from '../types'

interface LegalState {
  consentStatus: CookieConsentStatus
  preferences: CookiePreferences
  consentTimestamp: string | null
  showBanner: boolean

  // Actions
  acceptAll: () => void
  rejectAll: () => void
  customizePreferences: (preferences: CookiePreferences) => void
  dismissBanner: () => void
  resetConsent: () => void
}

export const useLegalStore = createWithEqualityFn<LegalState>()(
  persist(
    (set) => ({
      consentStatus: 'pending',
      preferences: { ...DEFAULT_COOKIE_PREFERENCES },
      consentTimestamp: null,
      showBanner: true,

      acceptAll: () => {
        set({
          consentStatus: 'accepted',
          preferences: {
            necessary: true,
            analytics: true,
            marketing: true,
          },
          consentTimestamp: new Date().toISOString(),
          showBanner: false,
        })
      },

      rejectAll: () => {
        set({
          consentStatus: 'rejected',
          preferences: {
            necessary: true,
            analytics: false,
            marketing: false,
          },
          consentTimestamp: new Date().toISOString(),
          showBanner: false,
        })
      },

      customizePreferences: (preferences) => {
        set({
          consentStatus: 'customized',
          preferences: { ...preferences, necessary: true },
          consentTimestamp: new Date().toISOString(),
          showBanner: false,
        })
      },

      dismissBanner: () => {
        set({ showBanner: false })
      },

      resetConsent: () => {
        set({
          consentStatus: 'pending',
          preferences: { ...DEFAULT_COOKIE_PREFERENCES },
          consentTimestamp: null,
          showBanner: true,
        })
      },
    }),
    {
      name: 'docurious-legal',
      storage: createJSONStorage(() => localStorage),
    },
  ),
  shallow,
)

export const selectConsentStatus = (state: LegalState) => state.consentStatus
export const selectCookiePreferences = (state: LegalState) => state.preferences
export const selectShowBanner = (state: LegalState) => state.showBanner
